import React, { useState, useEffect } from "react";
import { X, Send, AlertCircle } from "lucide-react";

const GRADES = ["M-30", "S-30", "L-30", "M-31", "S-31", "L-31", "Raw Sugar"];
const SEASONS = ["2024-25", "2025-26", "2026-27"];

const EditResubmitModal = ({ submission, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    millName: "",
    grade: "",
    season: "",
    rateExcl: "",
    qty: "",
    paymentDate: "",
    liftingDate: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (submission) {
      setForm({
        millName: submission?.millName || "",
        grade: submission?.grade || "",
        season: submission?.season || "",
        rateExcl: submission?.rateExcl || "",
        qty: submission?.qty || "",
        paymentDate: submission?.paymentDate || "",
        liftingDate: submission?.liftingDate || "",
      });
    }
  }, [submission]);

  const GST_RATE = 0.05;
  const rateIncl = form?.rateExcl
    ? Math.round(Number(form?.rateExcl) * (1 + GST_RATE))
    : "";

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await onSubmit?.({ ...form, rateIncl });
    setSubmitting(false);
    if (result?.error) {
      setError(result?.error);
      return; // stay open so the officer can see the real error and retry
    }
    onClose?.();
  };

  if (!submission) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ background: "#faf7f2", borderColor: "#f3e8d0" }}
        >
          <div>
            <h2 className="text-base font-bold text-[#1f2937]">
              Submit Corrected Rate
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{submission?.millName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[#f3e8d0] transition-colors"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Mill Name — not editable; a resubmission always keeps the same mill_id as the original */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Mill Name
            </label>
            <input
              type="text"
              value={form?.millName}
              readOnly
              className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* Error banner — shown on a failed resubmit, modal stays open so the officer can retry */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: "#b91c1c" }} />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Grade & Season */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Grade
              </label>
              <select
                value={form?.grade}
                onChange={(e) => handleChange("grade", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                required
              >
                <option value="">Select grade</option>
                {GRADES?.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Season
              </label>
              <select
                value={form?.season}
                onChange={(e) => handleChange("season", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                required
              >
                <option value="">Select season</option>
                {SEASONS?.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Rate Excl & Incl */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Rate (Excl GST) ₹
              </label>
              <input
                type="number"
                value={form?.rateExcl}
                onChange={(e) => handleChange("rateExcl", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
                min={0}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Rate (Incl GST) ₹
              </label>
              <input
                type="text"
                value={rateIncl ? `₹${Number(rateIncl)?.toLocaleString("en-IN")}` : ""}
                readOnly
                className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                placeholder="Auto-calculated"
              />
            </div>
          </div>

          {/* Qty */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Quantity (MT)
            </label>
            <input
              type="number"
              value={form?.qty}
              onChange={(e) => handleChange("qty", e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
              required
              min={0}
            />
          </div>

          {/* Payment & Lifting Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Payment Date
              </label>
              <input
                type="date"
                value={form?.paymentDate}
                onChange={(e) => handleChange("paymentDate", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Lifting Date
              </label>
              <input
                type="date"
                value={form?.liftingDate}
                onChange={(e) => handleChange("liftingDate", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[#e8ddd0] rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-bold text-white rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg, #d97706, #92400e)" }}
          >
            <Send size={15} />
            {submitting ? "Submitting…" : "Resubmit for Approval"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditResubmitModal;
